import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// ---- Product handle map (resolved 2026-08-05 against live Shopify store) ----
export const PDP_MAP = {
  "Talongames Type 99 Control Mousepad": "talongames-type-99-control-mousepad",
  "Talongames Type Zero Balanced Mousepad": "talongames-type-zero-balanced-mousepad",
  "ATK Blaze air - XSOFT Speed Mousepad": "atk-blaze-air-xsoft-speed-mousepad",
  "ARTISAN FX Series Mousepads XL (49 cm x 42 cm)": "artisan-fx-series-mousepads-xl-49-cm-x-42-cm",
  "Talongames ULTRAPAD P1 Glass Mousepad (Free skates)": "talongames-ultrapad-p1-glass-mousepad-free-skates",
  "Talongames ULTRAPAD P2 Glass Mousepad (Free skates)": "talongames-ultrapad-p2-glass-mousepad-free-skates",
  "BYTHERE TGF1 Tempered Glass Mousepad": "bythere-tgf1-tempered-glass-mousepad",
  "ATK Dragonfly A9 Plus 2.0 | Pro Max | Ultra": "atk-dragonfly-a9-series",
  "ATK RS6 Air Magnetic Keyboard": "atk-rs6-air-magnetic-keyboard",
  "NICEHCK DB2 Hybrid IEM": "nicehck-db2-hybrid-iem",
  "Everglide AE68 Pro HE Keyboard": "everglide-ae68-pro-he-keyboard",
  "ATK X1 Air+": "atk-x1-air",
  "ATK A9 Air+": "atk-a9-air",
  "LEOBOG A80 RT Series": "leobog-a80-rt-series",
  "AULA HERO 68 | HERO68 HE Series": "aula-hero-68-hero68-he-series",
  "MCHOSE ACE 60 PRO Series | ACE 60 HE PRO": "mchose-ace-60-pro-series-ace-60-he-pro",
  "Everglide Su75 Pro Magnetic Keyboard": "everglide-su75-pro-magnetic-keyboard",
};

export const PLP_MAP = {
  "Best Sellers": "best-sellers",
  "Mice": "mice",
};

// Anchor with resolvable PDP handle — falls back to visible unresolved marker.
export function PdpLink({ name, children, fallbackPlp, className = "text-[#F59E0B] hover:underline" }) {
  const handle = PDP_MAP[name];
  const label = children || name;
  if (handle) {
    return (
      <Link to={`/p/${handle}`} className={className} data-testid={`operator-pdp-${handle}`}>
        {label}
      </Link>
    );
  }
  if (fallbackPlp && PLP_MAP[fallbackPlp]) {
    return (
      <Link to={`/collections/${PLP_MAP[fallbackPlp]}`} className={className} data-testid={`operator-plp-fallback-${PLP_MAP[fallbackPlp]}`}>
        {label}
      </Link>
    );
  }
  return (
    <span className="text-yellow-300/80 border-b border-yellow-300/40" data-testid="operator-link-unresolved">
      [{label} — link unresolved, flagged to operator]
    </span>
  );
}

export function PlpLink({ name, children, className = "text-[#F59E0B] hover:underline" }) {
  const handle = PLP_MAP[name];
  const label = children || name;
  if (!handle) {
    return <span className="text-yellow-300/80">[{label} — PLP unresolved]</span>;
  }
  return (
    <Link to={`/collections/${handle}`} className={className} data-testid={`operator-plp-${handle}`}>
      {label}
    </Link>
  );
}

// Injects Article + BlogPosting JSON-LD into <head> for the current article
export function useArticleJsonLd({ slug, headline, description, datePublished, dateModified }) {
  useEffect(() => {
    const url = `https://loadout.co.in/operator-lab/${slug}`;
    const payload = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": `${url}#article`,
          headline,
          description,
          author: { "@type": "Organization", name: "Loadout" },
          publisher: {
            "@type": "Organization",
            name: "Loadout",
            logo: { "@type": "ImageObject", url: "https://loadout.co.in/brand/loadout-bullet-mark.png" },
          },
          datePublished,
          dateModified: dateModified || datePublished,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          url,
        },
        {
          "@type": "BlogPosting",
          "@id": `${url}#blogposting`,
          headline,
          description,
          author: { "@type": "Organization", name: "Loadout" },
          publisher: {
            "@type": "Organization",
            name: "Loadout",
            logo: { "@type": "ImageObject", url: "https://loadout.co.in/brand/loadout-bullet-mark.png" },
          },
          datePublished,
          dateModified: dateModified || datePublished,
          url,
        },
      ],
    };
    const scriptId = `operator-lab-jsonld-${slug}`;
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = scriptId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
    return () => { el?.remove(); };
  }, [slug, headline, description, datePublished, dateModified]);
}

// Simple email capture (mocked submit — hooks to ESP once Workstream C lands)
export function EmailCaptureFooter() {
  return (
    <section className="mt-16 border-t border-white/10 pt-8" data-testid="operator-email-capture">
      <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F59E0B]">Buyer's Guide, Monthly</p>
      <h3 className="mt-2 font-display uppercase text-[22px] md:text-[26px] text-white leading-tight">
        Get our next buyer's guide by email. No spam, monthly only.
      </h3>
      <form
        className="mt-5 flex flex-col sm:flex-row gap-3 max-w-[520px]"
        onSubmit={(e) => { e.preventDefault(); const em = e.target.elements.email.value; if (em) alert(`Thanks — we'll add ${em} once the mailer lands.`); }}
        data-testid="operator-email-form"
      >
        <input
          type="email" name="email" required
          placeholder="you@example.com"
          data-testid="operator-email-input"
          className="flex-1 bg-black border border-white/20 focus:border-[#F59E0B] outline-none text-white px-4 py-3 font-mono text-sm min-h-[48px]"
        />
        <button
          type="submit"
          data-testid="operator-email-submit"
          className="bg-[#F59E0B] text-black hover:bg-[#FBBF24] font-mono text-[11px] tracking-[0.18em] uppercase px-6 py-3 min-h-[48px]"
        >
          Subscribe ➔
        </button>
      </form>
      <p className="mt-3 font-mono text-[10px] tracking-[0.12em] uppercase text-white/40">
        Mailer wiring lands with Workstream C — subscriptions are queued until then.
      </p>
    </section>
  );
}

// Article header (eyebrow, kicker, headline, meta)
export function ArticleHeader({ eyebrow, kicker, headline, subhead, readingTime, datePublished }) {
  return (
    <header className="mb-10">
      <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F59E0B]" data-testid="operator-article-eyebrow">{eyebrow}</p>
      {kicker && (
        <p className="mt-2 font-mono text-[11px] tracking-[0.14em] uppercase text-white/50">{kicker}</p>
      )}
      <h1 className="mt-4 font-display uppercase leading-[0.95] text-[36px] sm:text-[44px] lg:text-[56px] text-white" data-testid="operator-article-heading">
        {headline}
      </h1>
      {subhead && (
        <p className="mt-6 text-white/75 leading-relaxed text-[15px] max-w-[68ch]">{subhead}</p>
      )}
      <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] tracking-[0.16em] uppercase text-white/40">
        <span>{new Date(datePublished).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
        <span>·</span>
        <span>{readingTime}</span>
        <span>·</span>
        <span>Loadout Operator Lab</span>
      </div>
    </header>
  );
}

// Small nav back to /operator-lab
export function OperatorLabCrumb() {
  return (
    <nav className="mb-6 font-mono text-[10px] tracking-[0.18em] uppercase text-white/40" aria-label="Breadcrumb">
      <Link to="/operator-lab" className="hover:text-[#F59E0B]" data-testid="operator-lab-crumb">← Operator Lab</Link>
    </nav>
  );
}

// Final CTA row
export function ArticleCTA({ label = "Shop the collection", plpName = "Best Sellers" }) {
  const handle = PLP_MAP[plpName];
  return (
    <div className="mt-12 border-t border-white/10 pt-8">
      <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-white/50 mb-3">Continue in-store</p>
      <Link
        to={`/collections/${handle}`}
        data-testid="operator-article-cta"
        className="inline-block border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black font-mono text-[11px] tracking-[0.18em] uppercase px-5 py-3 min-h-[48px]"
      >
        {label} ➔
      </Link>
    </div>
  );
}


// ---- Article registry (single source of truth for index + related strip) ----
export const ARTICLE_META = [
  {
    slug: "aim-theory-101",
    category: "Tech Deep-Dive",
    title: "Aim Theory 101: Tracking, Flicks, Micro-Corrections — and What Mouse Shape Has to Do With Any of It",
    excerpt: "Aim isn't one skill — it's three. We break down tracking, flicks and micro-corrections, map hand size to mouse length, and show which shape, weight, hump and grip tension the pros actually use.",
    readingTime: "12 min read",
  },
  {
    slug: "the-grip-index",
    category: "Guide",
    title: "The Grip Index — How Surface Choice Affects Your Aiming",
    excerpt: "Speed pads, control pads, glass pads, cloth pads — a field guide to which mousepad surface fits your aim, broken down for Indian competitive players.",
    readingTime: "6 min read",
  },
  {
    slug: "15000-loadout-build",
    category: "Buyer's Notes",
    title: "Building a ₹15,000 Loadout That Doesn't Feel Like ₹15,000",
    excerpt: "Three complete peripherals builds at the ₹15,000 mark — mouse, keyboard, pad, IEM combos explained for Indian PC gamers.",
    readingTime: "7 min read",
  },
  {
    slug: "hall-effect-vs-mechanical",
    category: "Tech Deep-Dive",
    title: "Hall Effect vs Mechanical — A Clinical Comparison",
    excerpt: "Latency, actuation curves, durability, rapid trigger — with verified specs from four HE boards on Indian shelves today.",
    readingTime: "8 min read",
  },
];

// ---- Anonymous reactions (3-emoji set, per-browser toggle) ----

const _LAB_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const _VIEWER_KEY = "loadout-lab-viewer";
const _REACTIONS = [
  { key: "helpful", emoji: "👍", label: "Helpful" },
  { key: "learned", emoji: "🧠", label: "Learned something" },
  { key: "loved", emoji: "🔥", label: "Loved it" },
];

function _viewerId() {
  try {
    let v = window.localStorage.getItem(_VIEWER_KEY);
    if (!v) {
      v = (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
          `v-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      window.localStorage.setItem(_VIEWER_KEY, v);
    }
    return v;
  } catch {
    return "anonymous-no-storage";
  }
}

export function ArticleReactions({ slug }) {
  const [totals, setTotals] = useState({ helpful: 0, learned: 0, loved: 0 });
  const [mine, setMine] = useState([]);
  const [pending, setPending] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(
          `${_LAB_BACKEND}/api/lab/reactions?slug=${encodeURIComponent(slug)}&viewer=${encodeURIComponent(_viewerId())}`
        );
        if (!r.ok) return;
        const j = await r.json();
        if (!alive) return;
        setTotals(j.totals || {});
        setMine(j.mine || []);
      } catch { /* reactions are non-critical */ }
    })();
    return () => { alive = false; };
  }, [slug]);

  const toggle = async (key) => {
    if (pending) return;
    setPending(key);
    // Optimistic flip
    const wasActive = mine.includes(key);
    setMine((m) => (wasActive ? m.filter((x) => x !== key) : [...m, key]));
    setTotals((t) => ({ ...t, [key]: Math.max(0, (t[key] || 0) + (wasActive ? -1 : 1)) }));
    try {
      const r = await fetch(`${_LAB_BACKEND}/api/lab/reactions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, emoji: key, viewer: _viewerId() }),
      });
      if (r.ok) {
        const j = await r.json();
        setTotals(j.totals || {});
        setMine(j.mine || []);
      }
    } catch { /* keep optimistic state */ }
    setPending("");
  };

  return (
    <section className="mt-12 border-t border-white/10 pt-8" data-testid="lab-reactions">
      <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F59E0B]">Rate this guide</p>
      <div className="mt-4 flex flex-wrap gap-3">
        {_REACTIONS.map((r) => {
          const active = mine.includes(r.key);
          return (
            <button
              key={r.key}
              type="button"
              onClick={() => toggle(r.key)}
              disabled={pending === r.key}
              data-testid={`lab-reaction-${r.key}`}
              aria-pressed={active}
              className={
                "inline-flex items-center gap-2 border font-mono text-[11px] tracking-[0.12em] uppercase px-4 py-2.5 min-h-[44px] transition-colors " +
                (active
                  ? "border-[#F59E0B] bg-[#F59E0B] text-black"
                  : "border-white/20 text-white/70 hover:border-[#F59E0B] hover:text-[#F59E0B]")
              }
            >
              <span aria-hidden>{r.emoji}</span>
              <span>{r.label}</span>
              <span data-testid={`lab-reaction-count-${r.key}`} className={active ? "text-black/70" : "text-white/40"}>
                {totals[r.key] || 0}
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-3 font-mono text-[10px] tracking-[0.12em] uppercase text-white/40">
        Anonymous — one tap per reaction, tap again to undo.
      </p>
    </section>
  );
}

// ---- "Keep reading" strip — manual sibling mapping ----
const _RELATED_MAP = {
  "aim-theory-101": ["the-grip-index", "15000-loadout-build"],
  "the-grip-index": ["aim-theory-101", "15000-loadout-build"],
  "15000-loadout-build": ["aim-theory-101", "hall-effect-vs-mechanical"],
  "hall-effect-vs-mechanical": ["aim-theory-101", "15000-loadout-build"],
};

export function RelatedArticles({ currentSlug }) {
  const slugs = _RELATED_MAP[currentSlug] || [];
  const items = slugs
    .map((s) => ARTICLE_META.find((a) => a.slug === s))
    .filter(Boolean);
  if (!items.length) return null;
  return (
    <section className="mt-12 border-t border-white/10 pt-8" data-testid="keep-reading-strip">
      <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F59E0B]">Keep reading</p>
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {items.map((a) => (
          <Link
            key={a.slug}
            to={`/operator-lab/${a.slug}`}
            data-testid={`keep-reading-${a.slug}`}
            className="op-plain group block border border-white/10 hover:border-[#F59E0B] bg-white/[0.02] hover:bg-white/[0.04] transition-colors p-5"
          >
            <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-white/50 group-hover:text-[#F59E0B] transition-colors">
              {a.category} · {a.readingTime}
            </div>
            <h3 className="mt-3 font-display uppercase leading-tight text-[18px] text-white group-hover:text-[#F59E0B] transition-colors">
              {a.title}
            </h3>
            <p className="mt-3 text-white/60 leading-relaxed text-[13px]">
              {a.excerpt}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
}
